/**
 * Deserialization + sanitization of a portable selection snapshot against a target data context:
 * converts category names back to numeric IDs, drops steps/ids that no longer resolve against the
 * target, and produces a `TpSnapshotApplicationPlan` ready to be applied (see
 * ./dataContextSelectionSnapshot.ts). See ./index.ts for the feature-level overview and the
 * "manual diff" design rationale.
 */

import { TpCanonicalTypeInfo, TpDataContext, TpDataTable, TpDataTableColumn } from '../../interfaces';
import { getDataTableByCanonicalType } from '../../../features/visual-analytics/charts/chartDataBindings';
import {
  TpEnSelectionFilterStepCategorical,
  TpEnSelectionFilterStepDualCategorical,
  TpEnSelectionFilterStep,
  TpEnSelectionFilterStepImport,
  TpEnSelectionFilterTypes,
  TpEnSelectionFilterStepIntersections,
  TpEnSelectionFilterStepNumericalRange,
  TpEnSelectionFilterStepMultiLevelCat,
  TpEnSelectionFilterStepLasso2D,
  TpEnSelectionFilterStepManual,
} from '../interfaces';
import {
  TpSelectionFilterStep,
  TpCanonicalTypeSelection,
  TpSelectionStoragePayloadV1,
} from './selectionFilterSerialization.schemas';
import { TpCanonicalTypeSnapshotApplicationPlan, TpSnapshotApplicationPlan } from './snapshotApplicationPlan';
import { resolveColumns, sanitizeItemIdsAgainstTarget, tryConvertCatNameToCatIndex } from './stepSanitization';

/**
 * Converts a list of category display names to numeric indices for `colInfo`, pushing a warning
 * and excluding any names that do not exist in the target column.
 */
function convertCategoryNamesToIds(
  categoryNames: string[],
  colInfo: TpDataTableColumn,
  warnings: string[],
  categoryType: 'selected' | 'excluded',
): number[] {
  return categoryNames
    .map((catName) => {
      const catId = tryConvertCatNameToCatIndex(colInfo, catName);
      if (catId === null) {
        warnings.push(
          `dropped ${categoryType} category "${catName}" in step because it was not found in the target column.`,
        );
        return null;
      }
      return catId;
    })
    .filter((catId) => catId !== null) as number[];
}

function hydrateCategoricalStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  const step = { ...schemaStep } as TpEnSelectionFilterStepCategorical;
  const resolved = resolveColumns(dataContext, step.dataTableId, [step.colId]);
  if (!resolved) return { step: null, warnings: [`dropped step because the referenced property was not found.`] };
  const [colInfo] = resolved.columns;

  const warnings: string[] = [];
  step.selectedCategoryIdList = convertCategoryNamesToIds(step.selectedCategoryIdList, colInfo, warnings, 'selected');
  step.excludedCategoryIdList = convertCategoryNamesToIds(step.excludedCategoryIdList, colInfo, warnings, 'excluded');
  if (step.selectedCategoryIdList.length === 0 && step.excludedCategoryIdList.length === 0) {
    warnings.push(`dropped step because no selected or excluded categories were found in the target column.`);
    return { step: null, warnings };
  }

  return { step, warnings };
}

function hydrateDualCategoricalStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  const step = { ...schemaStep } as TpEnSelectionFilterStepDualCategorical;
  const resolved = resolveColumns(dataContext, step.dataTableId, [step.colId1, step.colId2]);
  if (!resolved) {
    return { step: null, warnings: [`dropped step because one or both referenced properties were not found.`] };
  }
  const [colInfo1, colInfo2] = resolved.columns;

  const warnings: string[] = [];
  step.selectedCellsList = step.selectedCellsList
    .map((cell) => {
      const val1 = tryConvertCatNameToCatIndex(colInfo1, cell.val1);
      const val2 = tryConvertCatNameToCatIndex(colInfo2, cell.val2);
      if (val1 === null || val2 === null) {
        warnings.push(`Selection with value "${cell.val1}, ${cell.val2}" does not exist in the target dataset.`);
        return null;
      }
      return { val1, val2 };
    })
    .filter((cell) => cell !== null);

  if (step.selectedCellsList.length === 0) {
    warnings.push(`dropped step because no selected cells were found in the target columns.`);
    return { step: null, warnings };
  }

  return { step, warnings };
}

function hydrateIntersectionsStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  const step = { ...schemaStep } as TpEnSelectionFilterStepIntersections;
  const resolved = resolveColumns(dataContext, step.dataTableId, [step.colId]);
  if (!resolved) return { step: null, warnings: [`dropped step because the referenced property was not found.`] };
  const [colInfo] = resolved.columns;

  const warnings: string[] = [];
  step.intersections = step.intersections
    .map((intersection) => {
      const catIds = intersection.map((catName) => tryConvertCatNameToCatIndex(colInfo, catName));
      if (catIds.some((catId) => catId === null)) {
        warnings.push(`one or more categories in "${intersection}" do not exist in the target dataset.`);
        return null;
      }
      return catIds as number[];
    })
    .filter((intersection) => intersection !== null);

  if (step.intersections.length === 0) {
    warnings.push(`dropped step because no valid intersections were found in the target column.`);
    return { step: null, warnings };
  }

  return { step, warnings };
}

function hydrateNumericalRangeStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  const step = { ...schemaStep } as TpEnSelectionFilterStepNumericalRange;
  const resolved = resolveColumns(dataContext, step.dataTableId, [step.colId]);
  if (!resolved) return { step: null, warnings: [`dropped step because the referenced property was not found.`] };

  // No further sanitization needed for numerical range steps
  return { step, warnings: [] };
}

function hydrateMultiLevelCatStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  const step = { ...schemaStep } as TpEnSelectionFilterStepMultiLevelCat;
  const resolved = resolveColumns(dataContext, step.dataTableId, step.colIds);
  if (!resolved) {
    return { step: null, warnings: [`dropped step because one or more referenced properties were not found.`] };
  }
  const { dataTable } = resolved;

  const targetItemIdSet = new Set<string>(dataTable.columnData[dataTable.colItemId]);
  const warnings: string[] = [];
  step.selectedElements = step.selectedElements
    .map((element) => ({
      ...element,
      selectedItemIds: sanitizeItemIdsAgainstTarget(
        element.selectedItemIds,
        targetItemIdSet,
        warnings,
        'selected item IDs from an element',
      ),
    }))
    .filter((element) => element.selectedItemIds.length > 0);

  if (step.selectedElements.length === 0) {
    return { step: null, warnings: [`dropped step because no selected elements were found in the target.`] };
  }

  // No further sanitization needed for multi-level categorical steps
  return { step, warnings };
}

function hydrateLasso2DStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  const step = { ...schemaStep } as TpEnSelectionFilterStepLasso2D;
  const resolved = resolveColumns(dataContext, step.dataTableId, [step.colIdX, step.colIdY]);
  if (!resolved) {
    return { step: null, warnings: [`dropped step because one or both referenced properties were not found.`] };
  }
  const { dataTable } = resolved;

  const targetItemIdSet = new Set<string>(dataTable.columnData[dataTable.colItemId]);
  const warnings: string[] = [];
  const selectedItemIds = sanitizeItemIdsAgainstTarget(
    step.selectedItemIds,
    targetItemIdSet,
    warnings,
    'selected item IDs',
  );
  if (selectedItemIds.length === 0) {
    warnings.push(`dropped step because no selected item IDs were found in the target.`);
    return { step: null, warnings };
  }

  return { step: { ...step, selectedItemIds }, warnings };
}

function hydrateManualStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  const step = { ...schemaStep } as TpEnSelectionFilterStepManual;
  let dataTable: TpDataTable;

  try {
    dataTable = getDataTableByCanonicalType(dataContext, canonicalTypeInfo.id);
  } catch {
    return { step: null, warnings: [`dropped manual step because the referenced entity was not found.`] };
  }

  const targetItemIdSet = new Set<string>(dataTable.columnData[dataTable.colItemId]);
  const warnings: string[] = [];
  const selectedItemIds = sanitizeItemIdsAgainstTarget(step.selectedItemIds, targetItemIdSet, warnings, 'item IDs');
  if (selectedItemIds.length === 0) {
    warnings.push(`dropped manual step because no item IDs were found in the target.`);
    return { step: null, warnings };
  }

  return { step: { ...step, selectedItemIds }, warnings };
}

function hydrateImportStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  const step = { ...schemaStep } as TpEnSelectionFilterStepImport;
  let dataTable: TpDataTable;

  try {
    dataTable = getDataTableByCanonicalType(dataContext, canonicalTypeInfo.id);
  } catch {
    return { step: null, warnings: [`dropped import step because the referenced entity was not found.`] };
  }

  const targetItemIdSet = new Set<string>(dataTable.columnData[dataTable.colItemId]);
  const warnings: string[] = [];
  const selectedItemIds = sanitizeItemIdsAgainstTarget(
    step.selectedItemIds,
    targetItemIdSet,
    warnings,
    'imported item IDs',
  );
  if (selectedItemIds.length === 0) {
    warnings.push(`dropped import step because no imported item IDs were found in the target.`);
    return { step: null, warnings };
  }

  return { step: { ...step, selectedItemIds }, warnings };
}

/**
 * Hydrates a Zod-validated schema step into a Redux runtime step,
 * performing dataset lookups and structural transformations.
 */
export function hydrateFilterStep(
  schemaStep: TpSelectionFilterStep,
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
): { step: TpEnSelectionFilterStep | null; warnings: string[] } {
  switch (schemaStep.type) {
    case TpEnSelectionFilterTypes.CATEGORICAL:
      return hydrateCategoricalStep(schemaStep, dataContext);
    case TpEnSelectionFilterTypes.DUALCATEGORICAL:
      return hydrateDualCategoricalStep(schemaStep, dataContext);
    case TpEnSelectionFilterTypes.INTERSECTIONS:
      return hydrateIntersectionsStep(schemaStep, dataContext);
    case TpEnSelectionFilterTypes.NUMERICALRANGE:
      return hydrateNumericalRangeStep(schemaStep, dataContext);
    case TpEnSelectionFilterTypes.MULTILEVELCAT:
      return hydrateMultiLevelCatStep(schemaStep, dataContext);
    case TpEnSelectionFilterTypes.LASSO2D:
      return hydrateLasso2DStep(schemaStep, dataContext);
    case TpEnSelectionFilterTypes.MANUAL:
      return hydrateManualStep(schemaStep, dataContext, canonicalTypeInfo);
    case TpEnSelectionFilterTypes.IMPORT:
      return hydrateImportStep(schemaStep, dataContext, canonicalTypeInfo);
  }
}

/**
 * Collapses item-id-based steps (manual, lasso, multi-level-cat, import) and manual toggles into a
 * single IMPORT step. Used for cross-asset pastes, where the source and target item ids may not
 * refer to the same entities even though the asset *type* matches (see validateAndPrepareFilterStepsForTarget).
 */
function groupItemIdBasedStepsIntoImportStep(
  filterSteps: TpEnSelectionFilterStep[],
  manualIds: string[],
  sourceAssetId: string,
  sourceAssetName: string,
): TpEnSelectionFilterStep[] {
  const importSteps: TpEnSelectionFilterStep[] = [];
  const selectedItemIdsSet = new Set<string>();

  for (const step of filterSteps) {
    switch (step.type) {
      case TpEnSelectionFilterTypes.MANUAL:
      case TpEnSelectionFilterTypes.LASSO2D:
      case TpEnSelectionFilterTypes.IMPORT:
        for (const id of step.selectedItemIds) {
          selectedItemIdsSet.add(id);
        }
        break;
      case TpEnSelectionFilterTypes.MULTILEVELCAT:
        for (const element of step.selectedElements) {
          for (const id of element.selectedItemIds) {
            selectedItemIdsSet.add(id);
          }
        }
        break;
      default:
        importSteps.push(step);
    }
  }

  // Add all manually toggled IDs to the set of selected IDs
  for (const id of manualIds) {
    selectedItemIdsSet.add(id);
  }

  if (selectedItemIdsSet.size > 0) {
    const importStep: TpEnSelectionFilterStepImport = {
      type: TpEnSelectionFilterTypes.IMPORT,
      description: 'Imported from another asset of the same type',
      key: 'imported-selection',
      assetId: sourceAssetId,
      assetName: sourceAssetName,
      selectedItemIds: Array.from(selectedItemIdsSet),
    };
    importSteps.push(importStep);
  }

  return importSteps;
}

export function validateAndPrepareFilterStepsForTarget(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  filterSteps: TpSelectionFilterStep[],
  manualToggleIds: string[],
  sourceAssetId: string,
  sourceAssetName: string,
): { sanitizedSteps: TpEnSelectionFilterStep[]; sanitizedManualToggleIds: string[]; warnings: string[] } {
  const warnings: string[] = [];

  // ----- Sanitized steps -----
  let sanitizedSelectionSteps: TpEnSelectionFilterStep[] = [];
  for (const [index, step] of filterSteps.entries()) {
    const { step: sanitizedStep, warnings: stepWarnings } = hydrateFilterStep(step, dataContext, canonicalTypeInfo);
    if (sanitizedStep) {
      sanitizedSelectionSteps.push(sanitizedStep);
    }
    warnings.push(...stepWarnings.map((w) => `step ${index + 1}: ${w}`));
  }

  // ----- toggle IDs -----
  const dataTable = getDataTableByCanonicalType(dataContext, canonicalTypeInfo.id);
  const targetItemIdSet = new Set<string>(dataTable.columnData[dataTable.colItemId]);
  const sanitizedManualToggleIds = sanitizeItemIdsAgainstTarget(
    manualToggleIds,
    targetItemIdSet,
    warnings,
    'manually-toggled entities',
  );

  // ----- Check empty filter -----
  if (
    (filterSteps.length > 0 || manualToggleIds.length > 0) &&
    sanitizedSelectionSteps.length === 0 &&
    sanitizedManualToggleIds.length === 0
  ) {
    warnings.push(`filter was dropped as no steps passed validation on the target.`);
  }

  // ----- Cross-asset import: gather all item ID-based steps and manual toggles into a single IMPORT step -----
  if (sourceAssetId !== dataContext.assetId) {
    sanitizedSelectionSteps = groupItemIdBasedStepsIntoImportStep(
      sanitizedSelectionSteps,
      sanitizedManualToggleIds,
      sourceAssetId,
      sourceAssetName,
    );
    return { sanitizedSteps: sanitizedSelectionSteps, sanitizedManualToggleIds: [], warnings };
  }
  return { sanitizedSteps: sanitizedSelectionSteps, sanitizedManualToggleIds, warnings };
}

/**
 * Validates one canonical type's snapshot entry onto the corresponding (same-id) canonical type of
 * the target data context, deserialises + sanitizes + assigns the selection filter.
 *
 * There are two scenarios:
 * 1. Same asset ID: the source and target canonical types share the same entity IDs, so the
 * snapshot's rule-based steps, item ID-based steps and manual toggles are applied verbatim.
 * 2. Different asset ID but same asset type: the source and target canonical types may have
 * different entity IDs, so the snapshot's rule-based steps are applied verbatim,
 * but any item ID-based steps and manual toggles need to be sanitized against the target's entity IDs.
 * In this case, all item ID-based steps and manual toggles are degraded into a single IMPORT step,
 * which contains the matched IDs from the target asset.
 *
 * Returns the list of fault-tolerance warnings collected along the way.
 */
function validateAndPrepareSnapshotForCanonicalType(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  snapshotEntry: TpCanonicalTypeSelection,
  sourceAssetId: string,
  sourceAssetName: string,
): TpCanonicalTypeSnapshotApplicationPlan {
  const {
    sanitizedSteps: sanitizedRestrictionSteps,
    sanitizedManualToggleIds: sanitizedRestrictionManualIds,
    warnings: restrictionWarnings,
  } = validateAndPrepareFilterStepsForTarget(
    dataContext,
    canonicalTypeInfo,
    snapshotEntry.restrictionFilter?.steps ?? [],
    snapshotEntry.restrictionManualToggleIds ?? [],
    sourceAssetId,
    sourceAssetName,
  );
  const {
    sanitizedSteps: sanitizedSelectionSteps,
    sanitizedManualToggleIds,
    warnings: selectionWarnings,
  } = validateAndPrepareFilterStepsForTarget(
    dataContext,
    canonicalTypeInfo,
    snapshotEntry.selectionFilter.steps,
    snapshotEntry.manualToggleIds,
    sourceAssetId,
    sourceAssetName,
  );

  const warningPrefix = `Entity "${canonicalTypeInfo.name}"`;

  return {
    warnings: [
      ...restrictionWarnings.map((warning) => `${warningPrefix} restriction: ${warning}`),
      ...selectionWarnings.map((warning) => `${warningPrefix} selection: ${warning}`),
    ],
    restrictionFilterPlan: {
      filter:
        sanitizedRestrictionSteps.length > 0
          ? { steps: sanitizedRestrictionSteps, manualSelection: sanitizedRestrictionManualIds.length > 0 }
          : null,
      manualToggleIds: sanitizedRestrictionManualIds,
    },
    selectionFilterPlan: {
      filter: { steps: sanitizedSelectionSteps, manualSelection: sanitizedManualToggleIds.length > 0 },
      manualToggleIds: sanitizedManualToggleIds,
    },
  };
}

/**
 * Validates a snapshot against a target data context, and prepares a plan for applying it.
 * Returns a list of warnings and a per-canonical-type plan for applying the snapshot.
 * Does not mutate the target data context.
 */
export function validateAndPrepareSnapshot(
  dataContext: TpDataContext,
  snapshot: TpSelectionStoragePayloadV1,
): TpSnapshotApplicationPlan {
  const warnings: string[] = [];
  const canonicalTypeSelections: { [canonicalTypeId: string]: TpCanonicalTypeSnapshotApplicationPlan } = {};

  if (dataContext.assetId !== snapshot.sourceAssetId) {
    warnings.push(
      `Warning: applying the selection to a different asset can affect the selection's applicability for selections based on topology or relatedness.`,
    );
  }

  for (const [canonicalTypeId, snapshotEntry] of Object.entries(snapshot.canonicalTypeSelections)) {
    const canonicalTypeInfo = dataContext.canonicalTypes[canonicalTypeId];
    if (!canonicalTypeInfo) {
      warnings.push(`Entity "${snapshotEntry.canonicalTypeName}": not present in target data.`);
      continue;
    }
    const plan = validateAndPrepareSnapshotForCanonicalType(
      dataContext,
      canonicalTypeInfo,
      snapshotEntry,
      snapshot.sourceAssetId,
      snapshot.sourceAssetName,
    );
    canonicalTypeSelections[canonicalTypeId] = plan;
  }
  return { warnings, canonicalTypeSelections };
}
